import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function ArtifactSearch({ artifacts, officers, onFilteredResults }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedEditors, setSelectedEditors] = useState([]);
  const [selectedViewers, setSelectedViewers] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Get unique values for filters
  const allTags = [...new Set(artifacts.flatMap((a) => a.tags || []))];
  const allOwners = [...new Set(artifacts.map((a) => a.owner_email))];

  React.useEffect(() => {
    const filtered = artifacts.filter((artifact) => {
      // Text search (name, description, tags)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = artifact.name.toLowerCase().includes(query);
        const matchesDescription = artifact.description?.toLowerCase().includes(query);
        const matchesTags = artifact.tags?.some((tag) => tag.toLowerCase().includes(query));
        const matchesCategory = artifact.category.toLowerCase().includes(query);

        if (!matchesName && !matchesDescription && !matchesTags && !matchesCategory) {
          return false;
        }
      }

      // Category filter
      if (selectedCategory !== 'all' && artifact.category !== selectedCategory) {
        return false;
      }

      // Status filter
      if (selectedStatus !== 'all' && artifact.status !== selectedStatus) {
        return false;
      }

      // Owner filter
      if (selectedOwner !== 'all' && artifact.owner_email !== selectedOwner) {
        return false;
      }

      // Tags filter
      if (selectedTags.length > 0) {
        const hasAllTags = selectedTags.every((tag) => artifact.tags?.includes(tag));
        if (!hasAllTags) return false;
      }

      // Editors filter
      if (selectedEditors.length > 0) {
        const hasEditor = selectedEditors.some((editor) => artifact.editors?.includes(editor));
        if (!hasEditor) return false;
      }

      // Viewers filter
      if (selectedViewers.length > 0) {
        const hasViewer = selectedViewers.some((viewer) => artifact.viewers?.includes(viewer));
        if (!hasViewer) return false;
      }

      return true;
    });

    onFilteredResults(filtered);
  }, [searchQuery, selectedCategory, selectedStatus, selectedOwner, selectedTags, selectedEditors, selectedViewers, artifacts]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setSelectedOwner('all');
    setSelectedTags([]);
    setSelectedEditors([]);
    setSelectedViewers([]);
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || selectedStatus !== 'all' ||
  selectedOwner !== 'all' || selectedTags.length > 0 || selectedEditors.length > 0 || selectedViewers.length > 0;

  return (
    <Card className="glass-panel border-slate-800 mb-6">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input data-testid="art-artifactsearch-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="  Search artifacts by name, description, tags, or category..." 
              className="bg-slate-50 pl-10 border-slate-800 text-slate-900 placeholder:text-slate-500" />
              

          </div>
          <Button
            variant="outline"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="border-slate-800">

            <Filter className="w-4 h-4 mr-2" />
            Advanced
            {showAdvanced ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </Button>
          {hasActiveFilters &&
          <Button data-testid="art-clear-btn" variant="ghost" onClick={clearFilters} className="text-slate-400">
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
          }
        </div>

        {showAdvanced &&
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-800">
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="bg-slate-50 border-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="funnel">Funnel</SelectItem>
                  <SelectItem value="dashboard">Dashboard</SelectItem>
                  <SelectItem value="calendar">Calendar</SelectItem>
                  <SelectItem value="protocol">Protocol</SelectItem>
                  <SelectItem value="sop">SOP</SelectItem>
                  <SelectItem value="template">Template</SelectItem>
                  <SelectItem value="framework">Framework</SelectItem>
                  <SelectItem value="checklist">Checklist</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="bg-slate-50 border-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="institutionalized">Institutionalized</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Owner</Label>
              <Select value={selectedOwner} onValueChange={setSelectedOwner}>
                <SelectTrigger className="bg-slate-50 border-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Owners</SelectItem>
                  {allOwners.map((email) => {
                  const officer = officers.find((o) => o.user_email === email);
                  return (
                    <SelectItem key={email} value={email}>
                        {officer?.callsign || email}
                      </SelectItem>);

                })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Tags</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-slate-50 border-slate-800">
                    {selectedTags.length === 0 ? 'Select tags...' : `${selectedTags.length} selected`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0 bg-slate-900 border-slate-800">
                  <Command>
                    <CommandInput placeholder="Search tags..." />
                    <CommandList>
                      <CommandEmpty>No tags found.</CommandEmpty>
                      <CommandGroup>
                        {allTags.map((tag) =>
                      <CommandItem
                        key={tag}
                        onSelect={() => {
                          setSelectedTags((prev) =>
                          prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
                          );
                        }}>

                            <div className="flex items-center gap-2 w-full">
                              <div className={`w-4 h-4 border rounded ${selectedTags.includes(tag) ? 'bg-indigo-600 border-indigo-600' : 'border-slate-600'}`} />
                              <span>{tag}</span>
                            </div>
                          </CommandItem>
                      )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedTags.length > 0 &&
            <div className="flex flex-wrap gap-1 mt-2">
                  {selectedTags.map((tag) =>
              <Badge key={tag} variant="secondary" className="bg-indigo-500/10 text-indigo-400">
                      {tag}
                      <button onClick={() => setSelectedTags(selectedTags.filter((t) => t !== tag))} className="ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
              )}
                </div>
            }
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Has Editor</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-slate-50 border-slate-800">
                    {selectedEditors.length === 0 ? 'Select editors...' : `${selectedEditors.length} selected`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0 bg-slate-900 border-slate-800">
                  <Command>
                    <CommandInput placeholder="Search officers..." />
                    <CommandList>
                      <CommandEmpty>No officers found.</CommandEmpty>
                      <CommandGroup>
                        {officers.map((officer) =>
                      <CommandItem
                        key={officer.id}
                        onSelect={() => {
                          setSelectedEditors((prev) =>
                          prev.includes(officer.user_email) ?
                          prev.filter((e) => e !== officer.user_email) :
                          [...prev, officer.user_email]
                          );
                        }}>

                            <div className="flex items-center gap-2 w-full">
                              <div className={`w-4 h-4 border rounded ${selectedEditors.includes(officer.user_email) ? 'bg-emerald-600 border-emerald-600' : 'border-slate-600'}`} />
                              <span>{officer.callsign}</span>
                            </div>
                          </CommandItem>
                      )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedEditors.length > 0 &&
            <div className="flex flex-wrap gap-1 mt-2">
                  {selectedEditors.map((email) => {
                const officer = officers.find((o) => o.user_email === email);
                return (
                  <Badge key={email} variant="secondary" className="bg-emerald-500/10 text-emerald-400">
                        {officer?.callsign || email}
                        <button onClick={() => setSelectedEditors(selectedEditors.filter((e) => e !== email))} className="ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>);

              })}
                </div>
            }
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Has Viewer</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-slate-50 border-slate-800">
                    {selectedViewers.length === 0 ? 'Select viewers...' : `${selectedViewers.length} selected`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0 bg-slate-900 border-slate-800">
                  <Command>
                    <CommandInput placeholder="Search officers..." />
                    <CommandList>
                      <CommandEmpty>No officers found.</CommandEmpty>
                      <CommandGroup>
                        {officers.map((officer) =>
                      <CommandItem
                        key={officer.id}
                        onSelect={() => {
                          setSelectedViewers((prev) =>
                          prev.includes(officer.user_email) ?
                          prev.filter((v) => v !== officer.user_email) :
                          [...prev, officer.user_email]
                          );
                        }}>

                            <div className="flex items-center gap-2 w-full">
                              <div className={`w-4 h-4 border rounded ${selectedViewers.includes(officer.user_email) ? 'bg-blue-600 border-blue-600' : 'border-slate-600'}`} />
                              <span>{officer.callsign}</span>
                            </div>
                          </CommandItem>
                      )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedViewers.length > 0 &&
            <div className="flex flex-wrap gap-1 mt-2">
                  {selectedViewers.map((email) => {
                const officer = officers.find((o) => o.user_email === email);
                return (
                  <Badge key={email} variant="secondary" className="bg-blue-500/10 text-blue-400">
                        {officer?.callsign || email}
                        <button onClick={() => setSelectedViewers(selectedViewers.filter((v) => v !== email))} className="ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>);

              })}
                </div>
            }
            </div>
          </div>
        }
      </CardContent>
    </Card>);

}
